import type { EntityKind } from '../types/api'

/**
 * The roles a person can sign up for and sign in as, with the words the platform uses for them.
 *
 * <p>One list, shared by the sign-in picker and the registration form's extra-role boxes. They have
 * to agree: someone who ticks "Parent / Guardian" on the way in and then cannot find that same
 * wording in the sign-in dropdown has no way to tell whether the role was created at all.
 *
 * <p>Deliberately not every {@link EntityKind}. CENTRE, PROSPECT and COUNSELLOR exist in the schema
 * and on old rows, but nothing offers them today - listing them would be inviting people into roles
 * with no form behind them.
 */
export interface RoleOption {
  kind: EntityKind
  /** What the person calls themselves. Matches the wording on the sign-up cards. */
  label: string
  /** For the sign-in dropdown, where there is room for a word of context but not a sentence. */
  hint: string
}

export const ROLE_OPTIONS: RoleOption[] = [
  { kind: 'STUDENT', label: 'Learner', hint: 'Find tutors nearby' },
  { kind: 'TUTOR', label: 'Tutor / Coach / Trainer', hint: 'Teach and get discovered' },
  { kind: 'GUARDIAN', label: 'Parent / Guardian', hint: 'Manage your children' },
  { kind: 'SCHOOL', label: 'School', hint: 'Your school on the map' },
  { kind: 'COLLEGE', label: 'College', hint: 'Reach learners choosing where to study' },
  { kind: 'COMPANY', label: 'Corporate', hint: 'Offer internships' },
]

/**
 * The label for a role, falling back to the raw kind.
 *
 * <p>The fallback matters for the roles above that are not offered: an old account really can be a
 * COUNSELLOR, and its role switcher has to name it rather than render an empty chip.
 */
export function roleLabel(kind: EntityKind): string {
  return ROLE_OPTIONS.find((option) => option.kind === kind)?.label ?? kind
}

/**
 * The extra roles a form may offer, given the one it is already registering.
 *
 * <p>Excludes the primary - the server ignores a duplicate, but offering someone a box for the
 * thing they are already filling in is a question with only one sensible answer.
 */
export function additionalRoleChoices(primary: EntityKind): RoleOption[] {
  return ROLE_OPTIONS.filter((option) => option.kind !== primary)
}
